import React, {MutableRefObject, useEffect, useRef, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFileImport} from "@fortawesome/pro-light-svg-icons";
import {Trans} from "react-i18next";
import {Button} from "@unleashed/components/ui/Button";
import {faFile} from "@fortawesome/pro-light-svg-icons";
import {t} from "i18next";
import classNames from "classnames";
import {Heading} from "../../ui/components/Heading";
import { displayFileSize } from "../../../utils/displayFileSizeUtil";
import { isInvalidFileSize } from "../../../utils/fileValidationUtils";

type UploadModalProps = {
  showModal:boolean;
  handleShowModal(bool:boolean):void
  acceptedFileTypes:string;
  maxFileSizeBytes:number
  modalHeader:string;
  onSubmit:(file:File, comment: string)=>void;
  allowComment?: boolean;
}

const FileUploadModal = ({
  showModal,
  handleShowModal,
  acceptedFileTypes,
  maxFileSizeBytes,
  modalHeader,
  onSubmit,
  allowComment = false
}: UploadModalProps) => {
  const [file, setFile] = useState<File>();
  const [dragActive, setDragActive] = useState<boolean>(false);
  const inputRef : MutableRefObject<HTMLInputElement|null> = useRef(null);

  useEffect(()=> {
    if (!showModal) {
      setFile(undefined);
      setComment("");
    }
    return () => {
      setFile(undefined);
      setComment("");
    };
  },[showModal]);

  const handleDrag = (event:React.DragEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === "dragenter" || event.type === "dragover")
      setDragActive(true);
    else if (event.type === "dragleave")
      setDragActive(false);
  };

  const handleDrop = (event:React.DragEvent<HTMLFormElement>) =>{
    if(dragActive) {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      if (event.dataTransfer?.files[0])
        setFile(event.dataTransfer.files[0]);
    }
  };

  const handleChange = (event:React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    if (event.target.files && event.target.files[0])
      setFile(event.target.files[0]);
  };

  const [comment, setComment] = useState<string>("");

  const handleSubmit = () => {
    // Support backward compatibility: if onSubmit expects only one argument, call it safely
    if (onSubmit.length === 1) {
      onSubmit(file as File, "");
    } else {
      (onSubmit as (file: File, comment?: string) => void)(file as File, allowComment ? comment : undefined);
    }
    handleShowModal(false);
  };

  return showModal ?
    (
      <div
        className="fixed top-0 left-0 bottom-0 right-0 h-screen w-screen backdrop-blur-sm flex items-center justify-center"
      >
        <form
          className="min-h-max bg-white border-2 border-unleashed-pink rounded -mt-16 max-w-xs w-80"
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onSubmit={e=>e.preventDefault()}
        >
          <div className="border-b-2 border-unleashed-pink py-2 px-4 w-full">
            <Heading className="text-4xl" size="lg">{modalHeader}</Heading>
          </div>
          <div className="flex flex-col p-4">
            <div
              className={classNames(
                "min-h-max border-tertiary border-dashed border-2 p-4 rounded opacity-75 w-full",
                "hover:opacity-100 transition"
              )}
            >
              <label htmlFor="upload-file" className="flex flex-col items-center gap-4 cursor-pointer">
                <FontAwesomeIcon icon={faFileImport} className="h-24 w-24 opacity-50 mr-6"/>
                {/*FIXME: Update for translation*/}
                <span>Select a file to upload</span>
                <small>or drag and drop it here</small>
                <input type="file" id="upload-file" name="upload-file" className="hidden" accept={acceptedFileTypes} onChange={handleChange} ref={inputRef}/>
              </label>
            </div>
            {file && isInvalidFileSize(file?.size ?? 0, maxFileSizeBytes) &&
              <div className="ml-2 pt-4 pb-1 text-unleashed-gray flex gap-4 items-center">
                <i className="text-danger">{t`Please choose a different file. File size cannot exceed `}{displayFileSize(maxFileSizeBytes)}.</i>
              </div>
            }
            {file && file.name &&
              <div className="ml-2 pt-4 pb-1 text-unleashed-gray flex gap-4 items-center max-w-xs">
                <FontAwesomeIcon icon={faFile} />
                <i className="truncate">{file.name}</i>
                <i>{displayFileSize(file.size)}</i>
              </div>
            }
            {allowComment && (
              <textarea
                placeholder="Enter a comment (optional)"
                className="border border-gray-300 rounded w-full p-2 mb-4 mt-4"
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            )}
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button"
                className="inline-flex items-center text-unleashed-pink bg-white hover:bg-primary hover:text-white transition"
                onClick={()=>handleShowModal(false)}
              >
                <Trans>Cancel</Trans>
              </Button>
              <Button
                type="button"
                disabled={isInvalidFileSize(file?.size ?? 0, maxFileSizeBytes)}
                className={classNames("inline-flex items-center",
                  !isInvalidFileSize(file?.size ?? 0, maxFileSizeBytes) ? "hover:bg-white hover:text-primary transition" : "cursor-not-allowed")}
                onClick={handleSubmit}
              >
                <Trans>Upload</Trans>
              </Button>
            </div>
          </div>
        </form>
      </div>
    ) : <></>;
};

export default FileUploadModal;
